import { useState, useCallback, useEffect, useRef } from 'react';
import { apiClient } from 'api';

export interface CvAnalysisStatusResponse {
  status: 'processing' | 'completed';
  results: any[];
  total_results: number;
  total_cvs: number;
  message: string;
  task_id: string;
}

export const useCvAnalysisStatus = () => {
  const [isPolling, setIsPolling] = useState(false);
  const [status, setStatus] = useState<'processing' | 'completed' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (conversationId: string | number, interval: number = 5000) => {
      stopPolling();
      setIsPolling(true);
      setStatus('processing');
      setError(null);

      const poll = async () => {
        try {
          const response = await apiClient.get<CvAnalysisStatusResponse>(
            '/assessment/cv-analysis/status/',
            { params: { conversation_id: conversationId } }
          );

          if (!isMountedRef.current) return;

          const data = response.data;
          setStatus(data.status);

          if (data.status === 'completed') {
            stopPolling();
          }
        } catch (err: any) {
          if (!isMountedRef.current) return;
          const errorMessage =
            err.response?.data?.message || err.message || 'Failed to check CV analysis status';
          setError(errorMessage);
          stopPolling();
        }
      };

      poll();
      pollingIntervalRef.current = setInterval(poll, interval);
    },
    [stopPolling]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  return {
    isPolling,
    status,
    error,
    startPolling,
    stopPolling,
  };
};
